import { makeAutoObservable } from 'mobx'
import { groupBy } from 'lodash'
import type { IDropdownItem } from 'shared/ui'
import { truthy } from 'shared/lib'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseInbox, type IResponseUnifiedInbox } from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox'
import { getDropdownItems, type ISendFromStoreItem } from 'widgets/SendFrom'
import { type IGetDropdownItemsOptions } from 'widgets/SendFrom/lib/getDropdownItems'

export type SendFromMultiStoreOptions = {
  getDropdownItemsOptions: IGetDropdownItemsOptions
  preloadedInboxes?: IResponseInbox[]
  isInboxMode?: boolean
}

export class SendFromMultiStore {
  initPromise
  private _onSelectionChange?: (selectedIds: number[]) => void
  private setFinishInit: ((value?: unknown) => void) | null = null
  constructor(private _entities: IEntitiesStore, private options?: SendFromMultiStoreOptions) {
    this.initPromise = new Promise((resolve) => {
      this.setFinishInit = resolve
    })
    this._init()
    makeAutoObservable(this)
  }

  setOnSelectionChange = (callback: (selectedIds: number[]) => void) => {
    this._onSelectionChange = callback
  }

  loading = true
  setLoading = (value: boolean) => {
    this.loading = value
  }

  private _itemsMap: Map<number | string, IDropdownItem<ISendFromStoreItem>> = new Map()
  private _excludeInboxIds: number[] = []

  setExcludeInboxIds = (excludeIds: number[]) => {
    this._excludeInboxIds = excludeIds
  }

  get dropdownItems(): IDropdownItem<ISendFromStoreItem>[] {
    return Array.from(this._itemsMap.values())
      .map((item) => ({
        ...item,
        children: item.children?.filter((child) => !this.selectedDropdownItemsMap.get(child.id)),
      }))
      .filter(
        (item) =>
          !this.selectedDropdownItemsMap.get(item.id) &&
          (this.options?.isInboxMode
            ? !this._excludeInboxIds.includes(item.data?.inbox?.id || 0)
            : true)
      )
      .filter(
        (item) =>
          !!item.children?.length ||
          (this.options?.isInboxMode ? item.data?.inbox : item.data?.number)
      )
  }

  get dropdownItemsByGroup() {
    const items = Array.from(this._itemsMap.values()).map((item) => ({
      ...item,
      children: item.children?.filter((child) => !this.selectedDropdownItemsMap.get(child.id)),
    }))

    return Object.entries(groupBy(items, 'group'))
  }

  private _isInboxResponse = (
    item: IResponseInbox | IResponseUnifiedInbox
  ): item is IResponseInbox => {
    return item.type !== 'unified-inbox'
  }

  private _init = async () => {
    let inboxes: Inbox[] = []
    if (this.options?.preloadedInboxes) {
      inboxes = this.options.preloadedInboxes.map((item) => new Inbox(item, this._entities))
    } else {
      const canManageOrgInboxes = this._entities.userPermissionsStore.getItem(
        'can_manage_organization_inboxes'
      )

      if (canManageOrgInboxes) {
        const { data } = await this._entities.inboxesStore.getTeamsAll()

        data.forEach((item) => {
          this._entities.numbersStore.addItems(item.numbers)
          inboxes.push(new Inbox(item, this._entities))
        })
      } else {
        const { data } = await InboxesApi.getInboxesPersonal()

        data.filter(this._isInboxResponse).forEach((item) => {
          this._entities.numbersStore.addItems(item.numbers)
          inboxes.push(new Inbox(item, this._entities))
        })
      }
    }

    const items: Array<IDropdownItem<ISendFromStoreItem>> = getDropdownItems(
      inboxes,
      this.options?.getDropdownItemsOptions
    )

    this._setItemsDropdownItems(items)
    this.setFinishInit?.()
    this.setLoading(false)
  }

  private _setItemsDropdownItems = (dropdownItems: IDropdownItem<ISendFromStoreItem>[]) => {
    this._itemsMap = new Map(dropdownItems.map((item) => [item.id, item]))
  }

  setDropdownItems(dropdownItems: IDropdownItem<ISendFromStoreItem>[]) {
    this._itemsMap.clear()
    this._setItemsDropdownItems(dropdownItems)
  }

  selectedDropdownItemsMap: Map<string | number, IDropdownItem<ISendFromStoreItem>> = new Map()
  get selectedDropdownItems() {
    return Array.from(this.selectedDropdownItemsMap.values())
  }
  setSelectedDropdownItem = (item: IDropdownItem<ISendFromStoreItem>) => {
    this.selectedDropdownItemsMap.set(item.id, item)
    this._onSelectionChange?.(this.selectedInboxIds)
  }
  removeSelectedDropdownItem = (item: IDropdownItem<ISendFromStoreItem>) => {
    this.selectedDropdownItemsMap.delete(item.id)
    this._onSelectionChange?.(this.selectedInboxIds)
  }

  handleMultiSelect = (items: IDropdownItem<ISendFromStoreItem>[]) => {
    items.forEach((item) => {
      this.setSelectedDropdownItem(item)
    })
  }

  handleMultiDeselect = (groupName: string) => {
    const allOriginalItems = Array.from(this._itemsMap.values())
    const parentItemsInGroup = allOriginalItems.filter((item) => item.group === groupName)
    const childrenInGroup = parentItemsInGroup.flatMap((parent) => parent.children || [])

    childrenInGroup.forEach((child) => {
      this.removeSelectedDropdownItem(child)
    })

    parentItemsInGroup.forEach((parent) => {
      this.removeSelectedDropdownItem(parent)
    })
  }

  setNumbersIds = async (ids: number[]) => {
    await this.initPromise
    const items: IDropdownItem<ISendFromStoreItem>[] = []
    const existNumberIds: Set<number> = new Set()

    this.dropdownItems.forEach((item) => {
      if (
        item.data?.number?.id &&
        ids.includes(item.data?.number?.id) &&
        !existNumberIds.has(item.data?.number?.id)
      ) {
        items.push(item)
        existNumberIds.add(item.data?.number?.id)
      }
      item.children?.forEach((child) => {
        if (ids.includes(child.data?.number?.id) && !existNumberIds.has(child.data?.number?.id)) {
          items.push(child)
          existNumberIds.add(child.data?.number?.id)
        }
      })
    })
    this.handleMultiSelect(items)
  }

  setInboxIds = async (inboxIds: number[]) => {
    await this.initPromise

    this.selectedDropdownItemsMap.clear()
    if (inboxIds.length === 0) return

    const items: IDropdownItem<ISendFromStoreItem>[] = []

    Array.from(this._itemsMap.values()).forEach((item) => {
      if (item.data?.inbox?.id && inboxIds.includes(item.data.inbox.id)) {
        items.push(item)
      }
    })

    if (items.length > 0) {
      this.handleMultiSelect(items)
    }
  }

  get selectedInboxIds() {
    return this.selectedDropdownItems.map((item) => item.data?.inbox?.id).filter(truthy)
  }

  error = ''
  setError = (value: string) => {
    this.error = value
  }
  clearError = () => {
    this.setError('')
  }

  get numbers() {
    return this.selectedDropdownItems.map((item) => item.data?.number).filter(truthy)
  }
  get numbers_ids() {
    return this.numbers.map((number) => number.id)
  }

  get isAircall() {
    return !!this.numbers.find((number) => number.is_aircall)
  }
}
